/**
 * Compact competitor data for programmatic SEO at scale.
 * Each competitor is defined with minimal inputs, and the full CompetitorData
 * is generated using sensible defaults based on the competitor type.
 *
 * This allows us to scale to 50+ comparison pages without hand-writing
 * full feature matrices for each one.
 */

import { type CompetitorData, type FeatureCategory } from './data';

export type CompetitorType = 'helpdesk' | 'itsm' | 'devtool' | 'project' | 'livechat' | 'crm';

export interface CompactCompetitor {
  slug: string;
  name: string;
  tagline: string;
  bestFor: string;
  type: CompetitorType;
  startingPrice: number; // per agent/month (0 = free)
  pricingModel: string;
  hasFreePlan: boolean | string;
  metaTitle?: string;
  metaDescription?: string;
  aiTicketStructuring: boolean | string;
  aiCopilot: boolean | string;
  aiAnalyst: boolean | string;
  publicApi: boolean | string;
  omnichannel: boolean | string;
  knowledgeBase: boolean | string;
  slaTracking: boolean | string;
  marketplace: string;
  euResidency: boolean | string;
  koraWins: string[];
  competitorWins: string[];
  switchingReasons: string[];
}

// ─── Compact competitors ──────────────────────────────────

export const compactCompetitors: CompactCompetitor[] = [
  // ─── Helpdesk tools ──────────────────────────────────
  {
    slug: 'kora-vs-help-scout',
    name: 'Help Scout',
    tagline: 'Shared inbox helpdesk for small teams',
    bestFor: 'Small teams wanting a simple shared inbox',
    type: 'helpdesk',
    startingPrice: 25, // Standard as listed 2026-10-21 (helpscout.com/pricing); annual toggle shows -16%
    pricingModel: 'From $25/user/mo',
    hasFreePlan: 'Yes (up to 5 users, 100 contacts/mo)',
    aiTicketStructuring: 'Add-on',
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: false,
    marketplace: '50+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'Pay-as-you-go credits', 'AI Analyst (1 credit per answer)', 'EU data residency', 'Unlimited members, no seat fees'],
    competitorWins: ['Shared inbox simplicity', 'Knowledge base', 'Live chat', 'Omnichannel'],
    switchingReasons: ['Per-seat pricing adds up fast', 'No AI ticket structuring', 'No AI Analyst', 'No free plan'],
  },
  {
    slug: 'kora-vs-intercom',
    name: 'Intercom',
    tagline: 'Customer messaging platform with AI chatbot',
    bestFor: 'SaaS companies wanting AI chatbot + ticketing',
    type: 'helpdesk',
    startingPrice: 29, // Essential as listed 2026-10-21 (intercom.com/pricing); Fin $0.99 per outcome
    pricingModel: 'From $29/seat/mo + $0.99 per Fin outcome',
    hasFreePlan: false,
    aiTicketStructuring: 'Fin AI add-on',
    aiCopilot: 'Fin AI add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '350+',
    euResidency: '~',
    koraWins: ['Pay-as-you-go credits', 'AI Analyst included free', 'Unlimited members, no seat fees', 'EU data residency'],
    competitorWins: ['AI chatbot (Fin)', 'Omnichannel', 'Product tours', 'Customer engagement'],
    switchingReasons: ['Expensive — per-seat + usage pricing', 'No AI Analyst', 'No free plan', 'Costs scale unpredictably'],
  },
  {
    slug: 'kora-vs-hubspot-service-hub',
    name: 'HubSpot Service Hub',
    tagline: 'Customer service hub within HubSpot CRM',
    bestFor: 'Teams already using HubSpot CRM',
    type: 'crm',
    startingPrice: 20,
    pricingModel: 'From $20/seat/mo (Starter)',
    hasFreePlan: 'Free tools (limited)',
    aiTicketStructuring: false,
    aiCopilot: 'Enterprise add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '1500+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'Pay-as-you-go pricing', 'AI Analyst (1 credit per answer)', 'EU data residency'],
    competitorWins: ['Full CRM integration', 'Marketing + sales + service', 'Omnichannel', 'Knowledge base'],
    switchingReasons: ['Expensive at scale', 'No AI ticket structuring', 'No AI Analyst', 'Per-seat pricing'],
  },
  {
    slug: 'kora-vs-liveagent',
    name: 'LiveAgent',
    tagline: 'Helpdesk with live chat and ticketing',
    bestFor: 'Teams needing live chat + ticketing on a budget',
    type: 'helpdesk',
    startingPrice: 15, // Starter annual, liveagent.com/pricing, checked 2026-10-21
    pricingModel: 'From $15/agent/mo, annual ($19 monthly)',
    hasFreePlan: 'No (30-day trial)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '200+',
    euResidency: true,
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Modern UI'],
    competitorWins: ['Live chat included', 'Free plan (7 agents)', 'EU data residency', 'Omnichannel'],
    switchingReasons: ['No AI features', 'Outdated UI', 'No AI Analyst', 'Limited automation'],
  },
  {
    slug: 'kora-vs-kayako',
    name: 'Kayako',
    tagline: 'Customer service and helpdesk platform',
    bestFor: 'Teams wanting live chat + ticketing',
    type: 'helpdesk',
    startingPrice: 0, // no per-agent price listed on kayako.com/pricing, checked 2026-10-21
    pricingModel: 'Contact sales (Kay AI agent: $1 per resolved ticket)',
    hasFreePlan: 'No (contact sales)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Live chat', 'Omnichannel', 'Knowledge base', 'SLA tracking'],
    switchingReasons: ['No AI features', 'No free plan', 'Per-seat pricing', 'No AI Analyst'],
  },
  {
    slug: 'kora-vs-front',
    name: 'Front',
    tagline: 'Shared inbox for team collaboration',
    bestFor: 'Teams managing shared email inboxes',
    type: 'helpdesk',
    startingPrice: 25, // Starter annual, front.com/pricing, checked 2026-10-21
    pricingModel: 'From $25/seat/mo, annual (Starter, up to 10 seats)',
    hasFreePlan: 'No (14-day trial)',
    aiTicketStructuring: false,
    aiCopilot: 'Add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: '~',
    slaTracking: false,
    marketplace: '100+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees', 'Pay-as-you-go pricing'],
    competitorWins: ['Shared inbox', 'Omnichannel', 'CRM integration', 'Team collaboration'],
    switchingReasons: ['No AI ticket structuring', 'No AI Analyst', 'No free plan', 'Per-seat pricing'],
  },
  {
    slug: 'kora-vs-hiver',
    name: 'Hiver',
    tagline: 'Gmail-based helpdesk for teams',
    bestFor: 'Teams wanting helpdesk inside Gmail',
    type: 'helpdesk',
    startingPrice: 25, // Growth annual, hiverhq.com/pricing, checked 2026-10-21
    pricingModel: 'From $25/user/mo, annual ($35 monthly)',
    hasFreePlan: 'No (free trial)',
    aiTicketStructuring: false,
    aiCopilot: 'Add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: '~',
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'AI Analyst (1 credit per answer)', 'Pay-as-you-go pricing', 'Modern standalone UI'],
    competitorWins: ['Gmail integration', 'No learning curve', 'Free plan (3 users)', 'Knowledge base'],
    switchingReasons: ['No AI ticket structuring', 'No AI Analyst', 'Per-seat pricing', 'Locked into Gmail'],
  },
  {
    slug: 'kora-vs-gorgias',
    name: 'Gorgias',
    tagline: 'Helpdesk for e-commerce brands',
    bestFor: 'Shopify and e-commerce stores',
    type: 'helpdesk',
    startingPrice: 10, // gorgias.com/pricing, checked 2026-10-21
    pricingModel: 'From $10/mo (Starter, 50 tickets), usage-based',
    hasFreePlan: 'No (7-day trial)',
    aiTicketStructuring: 'Add-on',
    aiCopilot: 'Add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '100+',
    euResidency: '~',
    koraWins: ['Pay-as-you-go credits', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees', 'EU data residency'],
    competitorWins: ['Shopify integration', 'E-commerce automations', 'Omnichannel', 'Revenue tracking'],
    switchingReasons: ['Usage-based pricing is unpredictable', 'No AI Analyst', 'No free plan', 'E-commerce focused only'],
  },
  {
    slug: 'kora-vs-reamaze',
    name: 'Reamaze',
    tagline: 'Helpdesk and live chat for SaaS',
    bestFor: 'SaaS teams wanting chat + ticketing',
    type: 'helpdesk',
    startingPrice: 29, // Basic monthly, reamaze.com/pricing, checked 2026-10-21
    pricingModel: 'From $29/staff/mo ($26 annual)',
    hasFreePlan: 'No (14-day trial)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Live chat', 'Omnichannel', 'Knowledge base', 'Built for SaaS'],
    switchingReasons: ['No AI features', 'No free plan', 'Per-seat pricing', 'No AI Analyst'],
  },
  {
    slug: 'kora-vs-groove',
    name: 'Groove',
    tagline: 'Simple helpdesk for small teams',
    bestFor: 'Small teams wanting simplicity',
    type: 'helpdesk',
    startingPrice: 12,
    pricingModel: 'From $12/user/mo (price not re-verified, check vendor)',
    hasFreePlan: 'No (14-day trial)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: '~',
    knowledgeBase: true,
    slaTracking: false,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Simplicity', 'Knowledge base', 'Affordable', 'Good for small teams'],
    switchingReasons: ['No AI features', 'No free plan', 'No AI Analyst', 'Limited features'],
  },
  {
    slug: 'kora-vs-happyfox',
    name: 'HappyFox',
    tagline: 'Helpdesk ticketing for teams',
    bestFor: 'Teams wanting a traditional helpdesk',
    type: 'helpdesk',
    startingPrice: 24, // happyfox.com/pricing, checked 2026-10-25
    pricingModel: 'Help Desk from $24/agent/mo (starting at; Service Desk $49, Contact Center Suite $99; billing basis not stated on page; checked 2026-10-25)',
    hasFreePlan: 'No (14-day trial)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Omnichannel', 'Knowledge base', 'SLA tracking', 'Mature platform'],
    switchingReasons: ['No AI features', 'No free plan', 'Expensive', 'No AI Analyst'],
  },
  {
    slug: 'kora-vs-deskpro',
    name: 'Deskpro',
    tagline: 'Helpdesk and live chat platform',
    bestFor: 'Teams wanting on-premise or cloud helpdesk',
    type: 'helpdesk',
    startingPrice: 39, // Team, deskpro.com/pricing, checked 2026-10-21
    pricingModel: 'From $39/agent/mo (Team, minimum 5 agents)',
    hasFreePlan: 'No (self-guided tour)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '~',
    euResidency: true,
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['On-premise option', 'Omnichannel', 'Knowledge base', 'EU data residency'],
    switchingReasons: ['No AI features', 'No free plan', 'Per-seat pricing', 'No AI Analyst'],
  },
  {
    slug: 'kora-vs-teamwork-desk',
    name: 'Teamwork Desk',
    tagline: 'Helpdesk within Teamwork project suite',
    bestFor: 'Teams using Teamwork project management',
    type: 'helpdesk',
    startingPrice: 9.5, // Starter annual, teamwork.com/desk/pricing, checked 2026-10-21
    pricingModel: 'From $9.50/user/mo, annual (Starter)',
    hasFreePlan: 'No (30-day trial)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: '~',
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Teamwork integration', 'Knowledge base', 'SLA tracking', 'Project management'],
    switchingReasons: ['No AI features', 'No free plan', 'Per-seat pricing', 'No AI Analyst'],
  },
  {
    slug: 'kora-vs-crisp',
    name: 'Crisp',
    tagline: 'Live chat and chatbot platform',
    bestFor: 'Teams wanting chat-first customer support',
    type: 'livechat',
    startingPrice: 45, // per workspace per month, crisp.chat/pricing, checked 2026-10-21
    pricingModel: 'Per workspace: Free (2 seats), Mini $45, Essentials $95, Plus $295 per month',
    hasFreePlan: 'Free (2 seats, 100 profiles)',
    aiTicketStructuring: false,
    aiCopilot: 'Add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: false,
    marketplace: '~',
    euResidency: true,
    koraWins: ['AI ticket structuring (free)', 'AI Analyst (1 credit per answer)', 'Issue tracking focus', 'Embedded sessions'],
    competitorWins: ['Flat pricing (unlimited seats)', 'Live chat', 'Chatbot', 'EU data residency'],
    switchingReasons: ['Not issue tracking focused', 'No AI Analyst', 'No AI ticket structuring', 'Chat-only orientation'],
  },
  {
    slug: 'kora-vs-tidio',
    name: 'Tidio',
    tagline: 'Live chat and chatbot for small businesses',
    bestFor: 'Small businesses wanting chat + chatbot',
    type: 'livechat',
    startingPrice: 24.17, // tidio.com/pricing, checked 2026-10-21
    pricingModel: 'From $24.17/mo (Starter); Free plan available',
    hasFreePlan: 'Free (50 conversations/mo, 10 seats)',
    aiTicketStructuring: false,
    aiCopilot: 'Add-on',
    aiAnalyst: false,
    publicApi: '~',
    omnichannel: '~',
    knowledgeBase: '~',
    slaTracking: false,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'AI Analyst (1 credit per answer)', 'Issue tracking focus', 'Public API'],
    competitorWins: ['Live chat', 'Chatbot', 'Free plan', 'Easy setup'],
    switchingReasons: ['Not issue tracking', 'No AI Analyst', 'No AI ticket structuring', 'Usage-based AI pricing'],
  },
  {
    slug: 'kora-vs-salesforce-service-cloud',
    name: 'Salesforce Service Cloud',
    tagline: 'CRM-native enterprise service desk',
    bestFor: 'Large enterprises using Salesforce',
    type: 'crm',
    startingPrice: 25,
    pricingModel: 'From $25/seat/mo (Starter)',
    hasFreePlan: false,
    aiTicketStructuring: 'Einstein AI add-on',
    aiCopilot: 'Einstein AI add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '5000+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'Pay-as-you-go pricing', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees', 'Simple setup'],
    competitorWins: ['Full CRM ecosystem', 'Omnichannel', 'Enterprise features', '5000+ apps'],
    switchingReasons: ['Extremely expensive at scale', 'Complex setup', 'No AI Analyst', 'Per-seat pricing', 'Overkill for small teams'],
  },
  {
    slug: 'kora-vs-servicenow',
    name: 'ServiceNow',
    tagline: 'Enterprise IT service management',
    bestFor: 'Large enterprises with ITIL processes',
    type: 'itsm',
    startingPrice: 100,
    pricingModel: 'From $100/agent/mo (minimum) (price not re-verified, check vendor)',
    hasFreePlan: false,
    aiTicketStructuring: 'Now Assist add-on',
    aiCopilot: 'Now Assist add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '1000+',
    euResidency: true,
    koraWins: ['AI ticket structuring (free)', 'Pay-as-you-go credits', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees', 'Simple setup'],
    competitorWins: ['Full ITSM suite', 'ITIL processes', 'Enterprise scale', 'Omnichannel'],
    switchingReasons: ['Extremely expensive', 'Complex implementation', 'Overkill for small teams', 'No AI Analyst', 'Months to set up'],
  },
  {
    slug: 'kora-vs-manageengine-servicedesk',
    name: 'ManageEngine ServiceDesk',
    tagline: 'IT helpdesk and asset management',
    bestFor: 'IT teams wanting helpdesk + asset management',
    type: 'itsm',
    startingPrice: 13, // manageengine.com/products/service-desk/pricing.html via search snippet, checked 2026-10-21
    pricingModel: 'From $13/technician/mo (Standard cloud)',
    hasFreePlan: 'Free edition (up to 5 technicians per ManageEngine)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: '~',
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Modern UI'],
    competitorWins: ['Asset management', 'Free plan (3 agents)', 'ITIL processes', 'SLA tracking'],
    switchingReasons: ['No AI features', 'Outdated UI', 'No AI Analyst', 'IT-focused only'],
  },
  {
    slug: 'kora-vs-sysaid',
    name: 'SysAid',
    tagline: 'ITSM and helpdesk for IT teams',
    bestFor: 'IT departments wanting ITSM',
    type: 'itsm',
    startingPrice: 89, // sysaid.com/pricing, checked 2026-10-21
    pricingModel: 'From $89/agent/mo (ITSM Professional)',
    hasFreePlan: 'No (trial available)',
    metaTitle: 'Kora vs SysAid: IT Ticketing Pricing Compared',
    metaDescription: 'SysAid ITSM Professional lists $89 per agent a month (checked 2026-10-21). Kora is $0.10 per ticket with unlimited members, SLAs and automation included.',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: '~',
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Asset management', 'ITIL processes', 'SLA tracking', 'On-premise option'],
    switchingReasons: ['No AI features', 'No free plan', 'Per-seat pricing', 'No AI Analyst'],
  },
  {
    slug: 'kora-vs-osticket',
    name: 'osTicket',
    tagline: 'Open-source ticketing system',
    bestFor: 'Teams wanting self-hosted, free ticketing',
    type: 'helpdesk',
    startingPrice: 0,
    pricingModel: 'Free (self-hosted)',
    hasFreePlan: 'Free (self-hosted)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: '~',
    omnichannel: false,
    knowledgeBase: '~',
    slaTracking: false,
    marketplace: '~',
    euResidency: 'Self-hosted',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'No hosting needed', 'Modern UI'],
    competitorWins: ['Free and open source', 'Self-hosted', 'Full control', 'No vendor lock-in'],
    switchingReasons: ['No AI features', 'Requires hosting', 'Outdated UI', 'No AI Analyst', 'Maintenance burden'],
  },
  {
    slug: 'kora-vs-linear',
    name: 'Linear',
    tagline: 'Issue tracking for product teams',
    bestFor: 'Product and engineering teams',
    type: 'devtool',
    startingPrice: 10, // Basic annual, linear.app/pricing, checked 2026-10-21
    pricingModel: 'From $10/user/mo, annual (Basic)',
    hasFreePlan: 'Free (unlimited members, 250 issues)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: false,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Customer support focus'],
    competitorWins: ['Fast keyboard-first UI', 'Git integration', 'Sprint planning', 'Product-focused'],
    switchingReasons: ['No AI ticket structuring', 'No AI Analyst', 'Not for customer support', 'Per-seat pricing'],
  },
  {
    slug: 'kora-vs-height',
    name: 'Height',
    tagline: 'AI-powered project management',
    bestFor: 'Teams wanting AI project management',
    type: 'project',
    startingPrice: 7,
    pricingModel: 'From $7/user/mo (price not re-verified, check vendor)',
    hasFreePlan: 'Free (limited)',
    aiTicketStructuring: false,
    aiCopilot: 'Basic AI',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: false,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'AI Analyst (1 credit per answer)', 'Customer support focus', 'Embedded sessions'],
    competitorWins: ['AI project management', 'Modern UI', 'Task automation', 'Affordable'],
    switchingReasons: ['Not for customer support', 'No AI Analyst', 'No AI ticket structuring', 'Project-focused only'],
  },
  {
    slug: 'kora-vs-clickup',
    name: 'ClickUp',
    tagline: 'All-in-one productivity and project management',
    bestFor: 'Teams wanting everything in one tool',
    type: 'project',
    startingPrice: 9, // Core annual per clickup.com/pricing (page also shows older $7 tier), checked 2026-10-21
    pricingModel: 'From $9/user/mo, annual (Core)',
    hasFreePlan: 'Free (unlimited members, 60MB storage)',
    aiTicketStructuring: false,
    aiCopilot: 'ClickUp AI add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: true,
    slaTracking: false,
    marketplace: '1000+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'AI Analyst (1 credit per answer)', 'Customer support focus', 'Simplicity'],
    competitorWins: ['All-in-one platform', 'Free plan', '1000+ integrations', 'Project management'],
    switchingReasons: ['Too complex for issue tracking', 'No AI Analyst', 'No AI ticket structuring', 'Feature overload'],
  },
  {
    slug: 'kora-vs-notion',
    name: 'Notion',
    tagline: 'All-in-one workspace and wiki',
    bestFor: 'Teams wanting flexible docs + tasks',
    type: 'project',
    startingPrice: 10,
    pricingModel: 'From $10/user/mo',
    hasFreePlan: 'Free (unlimited blocks for individuals)',
    aiTicketStructuring: false,
    aiCopilot: 'Notion AI add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: true,
    slaTracking: false,
    marketplace: '100+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'AI Analyst (1 credit per answer)', 'Customer support focus', 'Purpose-built for tickets'],
    competitorWins: ['Flexible workspace', 'Docs + tasks + wiki', 'Free plan', 'Notion AI'],
    switchingReasons: ['Not built for ticketing', 'No AI Analyst', 'No AI ticket structuring', 'DIY issue tracking'],
  },
  {
    slug: 'kora-vs-asana',
    name: 'Asana',
    tagline: 'Project and work management',
    bestFor: 'Teams wanting project management',
    type: 'project',
    startingPrice: 10.99, // Starter annual, asana.com/pricing, checked 2026-10-21
    pricingModel: 'From $10.99/user/mo, annual (Starter)',
    hasFreePlan: 'Free (Personal: 2 users)',
    aiTicketStructuring: false,
    aiCopilot: 'Asana AI add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: false,
    marketplace: '200+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'AI Analyst (1 credit per answer)', 'Customer support focus', 'Embedded sessions'],
    competitorWins: ['Project management', 'Free plan (15 users)', 'Timeline view', 'Workload management'],
    switchingReasons: ['Not for customer support', 'No AI Analyst', 'No AI ticket structuring', 'Project-focused only'],
  },
  {
    slug: 'kora-vs-monday',
    name: 'Monday.com',
    tagline: 'Work OS for project and work management',
    bestFor: 'Teams wanting visual project management',
    type: 'project',
    startingPrice: 9,
    pricingModel: 'From $9/seat/mo (3 seats min)',
    hasFreePlan: 'Free (2 seats)',
    aiTicketStructuring: false,
    aiCopilot: 'Monday AI add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: false,
    marketplace: '200+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'AI Analyst (1 credit per answer)', 'Customer support focus', 'No minimum seats'],
    competitorWins: ['Visual project management', 'Free plan (2 seats)', '200+ integrations', 'Customizable'],
    switchingReasons: ['Not for customer support', 'No AI Analyst', 'No AI ticket structuring', '3-seat minimum'],
  },
  {
    slug: 'kora-vs-trello',
    name: 'Trello',
    tagline: 'Kanban board for task management',
    bestFor: 'Small teams wanting simple kanban',
    type: 'project',
    startingPrice: 5,
    pricingModel: 'From $5/user/mo',
    hasFreePlan: 'Free (10 boards)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: false,
    marketplace: '200+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Customer support focus'],
    competitorWins: ['Simple kanban', 'Free plan (10 boards)', 'Easy to use', 'Affordable'],
    switchingReasons: ['No AI features', 'Not for customer support', 'No AI Analyst', 'Too simple for ticketing'],
  },
  {
    slug: 'kora-vs-jira',
    name: 'Jira',
    tagline: 'Issue and project tracking for dev teams',
    bestFor: 'Engineering teams using Atlassian',
    type: 'devtool',
    startingPrice: 0,
    pricingModel: 'Free (10 users), then from $8/user/mo (price not re-verified, check vendor)',
    hasFreePlan: 'Free (10 users)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: false,
    marketplace: '5000+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Customer support focus'],
    competitorWins: ['Free plan (10 users)', 'Agile/Scrum', '5000+ apps', 'Dev team focused'],
    switchingReasons: ['No AI features', 'Complex for non-devs', 'No AI Analyst', 'Not for customer support'],
  },
  {
    slug: 'kora-vs-youtrack',
    name: 'YouTrack',
    tagline: 'Issue tracker by JetBrains',
    bestFor: 'Engineering teams using JetBrains',
    type: 'devtool',
    startingPrice: 4,
    pricingModel: 'From $4/user/mo (price not re-verified, check vendor)',
    hasFreePlan: 'Free (10 users)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: true,
    slaTracking: false,
    marketplace: '~',
    euResidency: true,
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Customer support focus'],
    competitorWins: ['JetBrains integration', 'Free plan (10 users)', 'EU data residency', 'Agile boards'],
    switchingReasons: ['No AI features', 'Not for customer support', 'No AI Analyst', 'Dev-focused only'],
  },
  {
    slug: 'kora-vs-github-issues',
    name: 'GitHub Issues',
    tagline: 'Issue tracking within GitHub',
    bestFor: 'Open-source and dev teams on GitHub',
    type: 'devtool',
    startingPrice: 0, // github.com/pricing, checked 2026-10-21
    pricingModel: 'Free; Team $4/user/mo for the first 12 months',
    hasFreePlan: 'Free',
    aiTicketStructuring: false,
    aiCopilot: 'Copilot add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: '~',
    slaTracking: false,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Customer support focus'],
    competitorWins: ['Free for public repos', 'Git integration', 'Copilot', 'Dev ecosystem'],
    switchingReasons: ['Not for customer support', 'No AI Analyst', 'No AI ticket structuring', 'Dev-focused only'],
  },
  {
    slug: 'kora-vs-gitlab-issues',
    name: 'GitLab Issues',
    tagline: 'Issue tracking within GitLab',
    bestFor: 'Dev teams using GitLab CI/CD',
    type: 'devtool',
    startingPrice: 0, // about.gitlab.com/pricing, checked 2026-10-21
    pricingModel: 'Free; Premium $29/user/mo billed annually; Ultimate on request',
    hasFreePlan: 'Free (public repos)',
    aiTicketStructuring: false,
    aiCopilot: 'Duo add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: true,
    slaTracking: false,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Customer support focus'],
    competitorWins: ['Free for public repos', 'CI/CD integration', 'GitLab Duo AI', 'DevSecOps'],
    switchingReasons: ['Not for customer support', 'No AI Analyst', 'No AI ticket structuring', 'Dev-focused only'],
  },
  {
    slug: 'kora-vs-pagerduty',
    name: 'PagerDuty',
    tagline: 'Incident response and on-call management',
    bestFor: 'DevOps teams needing on-call alerting',
    type: 'devtool',
    startingPrice: 21,
    pricingModel: 'From $21/user/mo',
    hasFreePlan: 'Free (5 users)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: true,
    marketplace: '400+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['On-call alerting', 'Incident response', '400+ integrations', 'Free plan (5 users)'],
    switchingReasons: ['Not for ticket management', 'No AI Analyst', 'No AI ticket structuring', 'Incident-focused only'],
  },
  {
    slug: 'kora-vs-opsgenie',
    name: 'Opsgenie',
    tagline: 'Incident alerting by Atlassian (no longer sold since June 2025; support ends April 2027)',
    bestFor: 'Teams using Jira needing on-call',
    type: 'devtool',
    startingPrice: 9.45, // atlassian.com/software/opsgenie/pricing, checked 2026-10-21
    pricingModel: 'Essentials $9.45/user/mo annual; no longer sold since 2025-06-04, support ends 2027-04-05',
    hasFreePlan: 'Free (5 users); no longer sold',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: true,
    marketplace: '200+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['On-call alerting', 'Atlassian integration', 'Free plan (5 users)', 'SLA tracking'],
    switchingReasons: ['Not for ticket management', 'No AI Analyst', 'No AI ticket structuring', 'Alerting-focused only'],
  },
  {
    slug: 'kora-vs-sentry',
    name: 'Sentry',
    tagline: 'Error tracking and monitoring',
    bestFor: 'Engineering teams tracking code errors',
    type: 'devtool',
    startingPrice: 26,
    pricingModel: 'From $26/mo (usage-based)',
    hasFreePlan: 'Free (5K errors/mo)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: true,
    slaTracking: false,
    marketplace: '100+',
    euResidency: true,
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Customer support focus'],
    competitorWins: ['Error tracking', 'Stack traces', 'Release tracking', 'EU data residency'],
    switchingReasons: ['Not for customer support', 'No AI Analyst', 'No AI ticket structuring', 'Error-focused only'],
  },
  {
    slug: 'kora-vs-statuspage',
    name: 'Statuspage',
    tagline: 'Status page and incident communication',
    bestFor: 'Teams needing public status pages',
    type: 'devtool',
    startingPrice: 29,
    pricingModel: 'Free $0 (100 subscribers, 2 team members); Hobby $29 (5 team members); Startup $99; Business $300 (checked 2026-10-25)',
    hasFreePlan: 'Yes (100 subscribers, 2 team members)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: false,
    marketplace: '100+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Status page', 'Incident communication', 'Atlassian integration', 'Subscriber notifications'],
    switchingReasons: ['Not for ticket management', 'No AI Analyst', 'No AI ticket structuring', 'Status page only'],
  },
  {
    slug: 'kora-vs-incident-io',
    name: 'Incident.io',
    tagline: 'Incident management for Slack',
    bestFor: 'Teams running incidents from Slack',
    type: 'devtool',
    startingPrice: 15, // Team annual, incident.io/pricing, checked 2026-10-21
    pricingModel: 'From $15/user/mo, annual (Team; $19 monthly)',
    hasFreePlan: 'Free (Basic plan)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: true,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Slack-native incidents', 'On-call', 'Free plan (3 users)', 'Incident response'],
    switchingReasons: ['Not for ticket management', 'No AI Analyst', 'No AI ticket structuring', 'Incident-focused only'],
  },
  {
    slug: 'kora-vs-firehydrant',
    name: 'FireHydrant',
    tagline: 'Incident management platform',
    bestFor: 'SRE teams managing incidents',
    type: 'devtool',
    startingPrice: 25, // firehydrant.com/pricing, checked 2026-10-21
    pricingModel: 'Free up to 10 responders; Pro $25/responder/mo, annual',
    hasFreePlan: 'Free (10 responders)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: true,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Incident management', 'Runbooks', 'Free plan (5 users)', 'SRE focused'],
    switchingReasons: ['Not for ticket management', 'No AI Analyst', 'No AI ticket structuring', 'Incident-focused only'],
  },
  {
    slug: 'kora-vs-rootly',
    name: 'Rootly',
    tagline: 'Incident management on Slack',
    bestFor: 'Teams managing incidents in Slack',
    type: 'devtool',
    startingPrice: 20, // rootly.com/pricing, checked 2026-10-21
    pricingModel: 'From $20/user/mo (Incident Response)',
    hasFreePlan: 'No (2-week trial)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: true,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Slack-native', 'Incident response', 'Postmortems', 'Status pages'],
    switchingReasons: ['Not for ticket management', 'No AI Analyst', 'No AI ticket structuring', 'Incident-focused only'],
  },
  {
    slug: 'kora-vs-bugzilla',
    name: 'Bugzilla',
    tagline: 'Open-source bug tracking system',
    bestFor: 'Teams wanting self-hosted bug tracking',
    type: 'devtool',
    startingPrice: 0,
    pricingModel: 'Free (self-hosted)',
    hasFreePlan: 'Free (self-hosted)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: '~',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: false,
    marketplace: '~',
    euResidency: 'Self-hosted',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'No hosting needed', 'Modern UI'],
    competitorWins: ['Free and open source', 'Self-hosted', 'Mature', 'Mozilla heritage'],
    switchingReasons: ['No AI features', 'Requires hosting', 'Outdated UI', 'No AI Analyst', 'Maintenance burden'],
  },
  {
    slug: 'kora-vs-redmine',
    name: 'Redmine',
    tagline: 'Open-source project management and issue tracking',
    bestFor: 'Teams wanting self-hosted, flexible tracking',
    type: 'devtool',
    startingPrice: 0,
    pricingModel: 'Free (self-hosted)',
    hasFreePlan: 'Free (self-hosted)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: '~',
    omnichannel: false,
    knowledgeBase: '~',
    slaTracking: false,
    marketplace: '500+ plugins',
    euResidency: 'Self-hosted',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'No hosting needed', 'Modern UI'],
    competitorWins: ['Free and open source', 'Self-hosted', '500+ plugins', 'Flexible'],
    switchingReasons: ['No AI features', 'Requires hosting', 'Outdated UI', 'No AI Analyst', 'Maintenance burden'],
  },
  {
    slug: 'kora-vs-spiceworks',
    name: 'Spiceworks Help Desk',
    tagline: 'Free IT helpdesk for small teams',
    bestFor: 'Small IT teams wanting free helpdesk',
    type: 'itsm',
    startingPrice: 0,
    pricingModel: 'Free (ad-supported) (price not re-verified, check vendor)',
    hasFreePlan: 'Free (ad-supported)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: '~',
    omnichannel: '~',
    knowledgeBase: true,
    slaTracking: false,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'No ads', 'Modern UI'],
    competitorWins: ['Free', 'IT community', 'Network mapping', 'No cost'],
    switchingReasons: ['No AI features', 'Ad-supported', 'Outdated UI', 'No AI Analyst', 'IT-focused only'],
  },
  {
    slug: 'kora-vs-zendesk-talk',
    name: 'Zendesk Talk',
    tagline: 'Phone support within Zendesk',
    bestFor: 'Teams needing phone + ticket support',
    type: 'helpdesk',
    startingPrice: -1, // not sold standalone per agent, support.zendesk.com, checked 2026-10-21
    pricingModel: 'Included in Zendesk Suite plans; usage billed separately (Contact Center from $83/agent/mo)',
    hasFreePlan: 'No (bundled with Suite)',
    aiTicketStructuring: 'Add-on $50/agent',
    aiCopilot: 'Add-on $50/agent',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '1500+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'Pay-as-you-go credits', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Phone support', 'Omnichannel', '1500+ apps', 'Zendesk ecosystem'],
    switchingReasons: ['Expensive per-seat + usage', 'No AI Analyst', 'No free plan', 'Costs scale fast'],
  },
  {
    slug: 'kora-vs-freshcaller',
    name: 'Freshcaller',
    tagline: 'Cloud phone system by Freshworks',
    bestFor: 'Teams needing cloud phone support',
    type: 'helpdesk',
    startingPrice: 15, // freshworks.com/freshcaller-cloud-pbx/pricing, checked 2026-10-24: Growth $15, Pro $39, Enterprise $69 per agent/mo annual
    pricingModel: 'Free plan (no included call minutes); paid from $15/agent/mo annual (Growth $15, Pro $39, Enterprise $69) plus call minutes billed separately',
    hasFreePlan: 'Yes (no included call minutes); 14-day trial',
    metaTitle: 'Kora vs Freshcaller: Pricing Compared (Pay Per Ticket)',
    metaDescription: 'Freshcaller is a cloud phone system from $15 per agent a month plus call minutes. Kora is a pay-per-ticket helpdesk: $0.10, no seats. See how they differ.',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: '~',
    slaTracking: true,
    marketplace: '1000+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Cloud phone', 'Omnichannel', 'Freshworks ecosystem', 'Affordable calling'],
    switchingReasons: ['No AI features', 'No AI Analyst', 'Per-seat + usage pricing', 'Call minutes billed separately'],
  },
  {
    slug: 'kora-vs-talkdesk',
    name: 'Talkdesk',
    tagline: 'Cloud contact center platform',
    bestFor: 'Teams needing a full contact center',
    type: 'helpdesk',
    startingPrice: 75,
    pricingModel: 'From $75/user/mo (price not re-verified, check vendor)',
    hasFreePlan: 'No (demo only)',
    aiTicketStructuring: 'Add-on',
    aiCopilot: 'Add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '70+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'Pay-as-you-go credits', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Contact center', 'Omnichannel', 'AI Studio', 'Enterprise scale'],
    switchingReasons: ['Extremely expensive', 'No AI Analyst', 'No free plan', 'Overkill for small teams'],
  },
  {
    slug: 'kora-vs-aircall',
    name: 'Aircall',
    tagline: 'Cloud phone system for support teams',
    bestFor: 'Teams needing cloud phone + integrations',
    type: 'helpdesk',
    startingPrice: 30, // Essentials annual, aircall.io/pricing, checked 2026-10-21
    pricingModel: 'From $30/license/mo, annual (Essentials, minimum 3)',
    hasFreePlan: 'No (14-day trial)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: '~',
    knowledgeBase: false,
    slaTracking: false,
    marketplace: '100+',
    euResidency: true,
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Cloud phone', '100+ integrations', 'EU data residency', 'Call analytics'],
    switchingReasons: ['No AI features', 'No AI Analyst', 'Per-seat pricing', 'Phone-focused only'],
  },
  {
    slug: 'kora-vs-zendesk-suite',
    name: 'Zendesk Suite',
    tagline: 'Full omnichannel support suite',
    bestFor: 'Teams needing omnichannel support',
    type: 'helpdesk',
    startingPrice: 55,
    pricingModel: 'From $55/agent/mo',
    hasFreePlan: 'No (14-day trial)',
    aiTicketStructuring: 'Add-on $50/agent',
    aiCopilot: 'Add-on $50/agent',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '1500+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'Pay-as-you-go credits', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Full omnichannel', '1500+ apps', 'Knowledge base', 'Enterprise features'],
    switchingReasons: ['Expensive ($55+/agent)', 'No AI Analyst', 'No free plan', 'Per-seat pricing'],
  },
  {
    slug: 'kora-vs-freshdesk-omni',
    name: 'Freshdesk Omnichannel',
    tagline: 'Omnichannel support by Freshworks',
    bestFor: 'Teams needing phone + chat + email + social',
    type: 'helpdesk',
    startingPrice: 29,
    pricingModel: 'Growth $29, Pro $79, Enterprise $119 per agent/mo on annual billing (checked 2026-10-24)',
    hasFreePlan: 'No (14-day trial)',
    metaTitle: 'Kora vs Freshdesk Omni: Pricing Without Seats',
    metaDescription: 'Freshdesk Omni lists $29, $79 and $119 per agent a month on annual billing (checked 2026-10-24). Kora is $0.10 per ticket, unlimited members. Compare.',
    aiTicketStructuring: 'Add-on $29/agent',
    aiCopilot: 'Add-on $29/agent',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '1000+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'Pay-as-you-go credits', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Full omnichannel', '1000+ apps', 'Freshworks ecosystem', 'Phone + chat + social'],
    switchingReasons: ['Per-seat pricing', 'No AI Analyst', 'No free plan', 'Costs scale with team size'],
  },
  {
    slug: 'kora-vs-zoho-desk-omni',
    name: 'Zoho Desk Premium',
    tagline: 'Premium omnichannel support by Zoho',
    bestFor: 'Teams in Zoho ecosystem needing omnichannel',
    type: 'helpdesk',
    startingPrice: 40,
    pricingModel: 'From $40/agent/mo (price not re-verified, check vendor)',
    hasFreePlan: 'No (30-day trial)',
    aiTicketStructuring: 'Enterprise $40/agent',
    aiCopilot: 'Zia AI (Enterprise)',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'Pay-as-you-go credits', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Zoho ecosystem', 'Omnichannel', 'Zia AI', 'Knowledge base'],
    switchingReasons: ['Per-seat pricing', 'No AI Analyst', 'No free plan', 'Locked into Zoho ecosystem'],
  },
  {
    slug: 'kora-vs-jira-service-management-premium',
    name: 'Jira SM Premium',
    tagline: 'Premium ITSM by Atlassian',
    bestFor: 'Large teams needing advanced ITSM',
    type: 'itsm',
    startingPrice: 51, // annual billing, 11-15 agent tier, checked 2026-10-21
    pricingModel: 'From $48/agent/mo',
    hasFreePlan: 'No (free for 3 agents on Standard)',
    aiTicketStructuring: 'Standard+ included',
    aiCopilot: 'Standard+ included',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '1000+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'Pay-as-you-go credits', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Atlassian ecosystem', 'Advanced ITSM', '1000+ apps', 'Omnichannel'],
    switchingReasons: ['Expensive ($48/agent)', 'No AI Analyst', 'Complex setup', 'Per-seat pricing'],
  },
  {
    slug: 'kora-vs-userlike',
    name: 'Userlike',
    tagline: 'Live chat and messaging for businesses',
    bestFor: 'Teams wanting chat + messaging support',
    type: 'livechat',
    startingPrice: 29,
    pricingModel: 'From $29/mo (4 seats)',
    hasFreePlan: 'Free (1 seat, limited)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: false,
    marketplace: '~',
    euResidency: true,
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Live chat', 'WhatsApp integration', 'EU data residency', 'Free plan (1 seat)'],
    switchingReasons: ['No AI features', 'No AI Analyst', 'Not for ticket management', 'Chat-focused only'],
  },
  {
    slug: 'kora-vs-livechat',
    name: 'LiveChat',
    tagline: 'Live chat for sales and support',
    bestFor: 'Teams wanting chat-first support',
    type: 'livechat',
    startingPrice: 19, // Essential annual, livechat.com/pricing, checked 2026-10-21
    pricingModel: 'From $19/user/mo, annual ($25 monthly)',
    hasFreePlan: 'No (14-day trial)',
    aiTicketStructuring: false,
    aiCopilot: 'AI Assistant add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: false,
    marketplace: '200+',
    euResidency: true,
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Live chat', 'EU data residency', '200+ integrations', 'Chat analytics'],
    switchingReasons: ['No AI ticket structuring', 'No AI Analyst', 'No free plan', 'Chat-focused only'],
  },
  {
    slug: 'kora-vs-comm100',
    name: 'Comm100',
    tagline: 'Omnichannel customer engagement',
    bestFor: 'Teams needing chat + email + phone',
    type: 'livechat',
    startingPrice: 31, // Startup annual, comm100.com/pricing, checked 2026-10-21
    pricingModel: 'From $31/agent/mo, annual ($39 monthly; Live Chat Startup)',
    hasFreePlan: 'No (30-day trial)',
    aiTicketStructuring: false,
    aiCopilot: 'AI Assistant',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: true,
    knowledgeBase: true,
    slaTracking: true,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Omnichannel', 'Knowledge base', 'SLA tracking', 'Chatbot'],
    switchingReasons: ['No AI ticket structuring', 'No AI Analyst', 'No free plan', 'Per-seat pricing'],
  },
  {
    slug: 'kora-vs-olark',
    name: 'Olark',
    tagline: 'Live chat for small businesses',
    bestFor: 'Small businesses wanting simple chat',
    type: 'livechat',
    startingPrice: 29, // olark.com/pricing, checked 2026-10-21
    pricingModel: 'From $29/agent/mo (Live Chat)',
    hasFreePlan: 'No (14-day trial)',
    aiTicketStructuring: false,
    aiCopilot: false,
    aiAnalyst: false,
    publicApi: '~',
    omnichannel: false,
    knowledgeBase: false,
    slaTracking: false,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Simple live chat', 'Easy setup', 'Affordable', 'Chat transcripts'],
    switchingReasons: ['No AI features', 'No AI Analyst', 'No free plan', 'Chat-only, no ticketing'],
  },
  {
    slug: 'kora-vs-zendesk-sell',
    name: 'Zendesk Sell',
    tagline: 'Sales CRM by Zendesk',
    bestFor: 'Teams wanting sales + support in Zendesk',
    type: 'crm',
    startingPrice: 19,
    pricingModel: 'From $19/user/mo (price not re-verified, check vendor)',
    hasFreePlan: 'No (14-day trial)',
    aiTicketStructuring: false,
    aiCopilot: 'Add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: '~',
    knowledgeBase: true,
    slaTracking: false,
    marketplace: '1500+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Sales CRM', 'Zendesk ecosystem', '1500+ apps', 'Pipeline management'],
    switchingReasons: ['No AI ticket structuring', 'No AI Analyst', 'No free plan', 'Sales-focused, not support'],
  },
  {
    slug: 'kora-vs-freshsales',
    name: 'Freshsales',
    tagline: 'CRM by Freshworks',
    bestFor: 'Teams wanting CRM + Freshdesk integration',
    type: 'crm',
    startingPrice: 9, // freshworks.com/crm/pricing, checked 2026-10-25
    pricingModel: 'Growth $9, Pro $39, Enterprise $59 per user/mo billed annually; no free plan listed (checked 2026-10-25)',
    hasFreePlan: 'No (21-day trial)',
    aiTicketStructuring: false,
    aiCopilot: 'Freddy AI add-on',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: '~',
    knowledgeBase: false,
    slaTracking: false,
    marketplace: '1000+',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Sales CRM', 'Freshworks ecosystem', 'Freddy AI', 'Marketplace of 1000+ apps'],
    switchingReasons: ['No AI ticket structuring', 'No AI Analyst', 'Sales-focused, not support', 'Per-seat pricing'],
  },
  {
    slug: 'kora-vs-zoho-crm',
    name: 'Zoho CRM',
    tagline: 'CRM within Zoho ecosystem',
    bestFor: 'Teams wanting CRM + Zoho Desk integration',
    type: 'crm',
    startingPrice: 14,
    pricingModel: 'From $14/user/mo (price not re-verified, check vendor)',
    hasFreePlan: 'Free (3 users)',
    aiTicketStructuring: false,
    aiCopilot: 'Zia AI (Enterprise)',
    aiAnalyst: false,
    publicApi: 'All plans',
    omnichannel: '~',
    knowledgeBase: true,
    slaTracking: false,
    marketplace: '~',
    euResidency: '~',
    koraWins: ['AI ticket structuring (free)', 'SLA tracking and automation (free)', 'AI Analyst (1 credit per answer)', 'Unlimited members, no seat fees'],
    competitorWins: ['Zoho ecosystem', 'Free plan (3 users)', 'Zia AI', 'Sales automation'],
    switchingReasons: ['No AI ticket structuring', 'No AI Analyst', 'Sales-focused, not support', 'Per-seat pricing'],
  },
];

// ─── Generate full CompetitorData from compact format ──────────

function generateFeatures(c: CompactCompetitor): FeatureCategory[] {
  return [
    {
      category: 'Pricing',
      rows: [
        { label: 'Free to start', kora: '1,100 free credits', competitor: c.hasFreePlan },
        { label: 'Starting price', kora: 'Pay as you go — $1 = 10 credits, top up from $5', competitor: c.pricingModel },
        { label: 'Pricing model', kora: 'Pay-as-you-go credits (no per-seat)', competitor: c.type === 'helpdesk' || c.type === 'itsm' ? 'Per-seat' : 'Per-seat' },
      ],
    },
    {
      category: 'AI Features',
      rows: [
        { label: 'AI ticket structuring', kora: 'Free (no add-on fee)', competitor: c.aiTicketStructuring },
        { label: 'AI Copilot', kora: 'Kora AI: 1 credit per draft', competitor: c.aiCopilot },
        { label: 'AI Analyst', kora: 'Included (1 credit/answer)', competitor: c.aiAnalyst },
      ],
    },
    {
      category: 'Platform',
      rows: [
        { label: 'Public API', kora: '20+ endpoints, every workspace', competitor: c.publicApi },
        { label: 'Embedded sessions', kora: true, competitor: false },
        { label: 'EU data residency', kora: 'EU (Cloudflare D1)', competitor: c.euResidency },
      ],
    },
    {
      category: 'Support Channels',
      rows: [
        { label: 'Omnichannel', kora: 'Planned (email-to-ticket, Slack, live chat)', competitor: c.omnichannel },
        { label: 'Knowledge base', kora: true, competitor: c.knowledgeBase },
        { label: 'SLA tracking', kora: true, competitor: c.slaTracking },
        { label: 'Marketplace apps', kora: 'Not available (REST API and webhooks)', competitor: c.marketplace },
      ],
    },
  ];
}

function generateAtAGlance(c: CompactCompetitor) {
  return [
    { label: 'Free to start', kora: '1,100 free credits to start', competitor: c.hasFreePlan === true ? 'Yes' : c.hasFreePlan },
    { label: 'Starting price', kora: 'Pay as you go ($1 = 10 credits)', competitor: c.pricingModel },
    { label: 'AI ticket structuring', kora: 'Unlimited, free', competitor: c.aiTicketStructuring === true ? 'Yes' : c.aiTicketStructuring },
    { label: 'AI Analyst', kora: 'Included (1 credit/answer)', competitor: c.aiAnalyst === true ? 'Yes' : c.aiAnalyst },
    { label: 'Public API', kora: '20+ endpoints, every workspace', competitor: c.publicApi },
    { label: 'EU data residency', kora: 'Yes', competitor: c.euResidency },
  ];
}

function generateFaqs(c: CompactCompetitor) {
  return [
    {
      q: `Is Kora a good ${c.name} alternative?`,
      a: `Yes. Kora offers AI ticket structuring and an AI Analyst (text-to-SQL) — included, with usage billed as credits. ${c.name} ${c.startingPrice > 0 ? `starts at $${c.startingPrice}/agent/mo` : c.startingPrice === 0 && /free|self-hosted/i.test(c.pricingModel) ? 'is free' : 'does not publish a per-agent price'} ${c.aiTicketStructuring === false ? 'and has no AI ticket structuring' : 'but AI features often cost extra'}. Kora's pay-as-you-go credit pricing means no per-seat costs as your team grows.`,
    },
    {
      q: `How does Kora's pricing compare to ${c.name}?`,
      a: `Kora is pay-as-you-go: $1 buys 10 credits, a ticket costs 1 credit, and an AI Analyst answer costs 1 credit. You start with 1,100 free credits — no card required. ${c.name} uses ${c.pricingModel.toLowerCase()}. With Kora you never pay per seat — a 10-agent team doing ~300 tickets/month with AI answers pays roughly $40/mo. ${c.startingPrice > 0 ? `At 10 agents, ${c.name} costs $${c.startingPrice * 10}/mo.` : ''}`,
    },
    {
      q: `Can I migrate from ${c.name} to Kora?`,
      a: `Yes. Export your tickets from ${c.name} as CSV, then paste raw descriptions into Kora — AI structures them into clean tickets automatically. You can also use the Public API (20+ endpoints, available to every workspace) to import tickets, comments, and attachments programmatically.`,
    },
    {
      q: `Does Kora have AI features like ${c.name}?`,
      a: `Kora includes AI ticket structuring (free) and an AI Analyst for asking questions about your ticket data (1 credit per answer). ${c.aiTicketStructuring === false ? `${c.name} does not have AI ticket structuring.` : `${c.name} offers AI features, often as a paid add-on.`} With Kora's credits, you only pay for what you use.`,
    },
  ];
}

export function expandCompetitor(c: CompactCompetitor): CompetitorData {
  // Competitor list-price cost at a team size: listed price per agent per month x agents.
  // (0 or negative = no published per-agent price.)
  const costAt = (agents: number) => (c.startingPrice > 0 ? Math.round(agents * c.startingPrice) : 0);

  return {
    slug: c.slug,
    name: c.name,
    tagline: c.tagline,
    bestFor: c.bestFor,
    pricingModel: c.pricingModel,
    metaTitle: c.metaTitle,
    metaDescription: c.metaDescription,
    plans: [
      ...(c.hasFreePlan === true || (typeof c.hasFreePlan === 'string' && /^(free|yes)/i.test(c.hasFreePlan))
        ? [{ name: 'Free', price: 0, priceNote: typeof c.hasFreePlan === 'string' ? c.hasFreePlan : 'Free' }]
        : []),
      { name: 'Paid plans from', price: c.startingPrice, priceNote: c.pricingModel, highlight: true },
    ],
    costAt,
    verdictKora: `Kora wins on AI value and pricing simplicity. AI ticket structuring and the AI Analyst are included — usage billed as pay-as-you-go credits ($1 = 10 credits), with no per-seat pricing and no monthly commitment.`,
    verdictCompetitor: `${c.name} is a solid choice for ${c.bestFor.toLowerCase()}. ${c.competitorWins.slice(0, 2).join(' and ')} are genuine strengths. But ${c.switchingReasons[0].toLowerCase()}.`,
    koraWins: c.koraWins,
    competitorWins: c.competitorWins,
    switchingReasons: c.switchingReasons,
    faqs: generateFaqs(c),
    features: generateFeatures(c),
    atAGlance: generateAtAGlance(c),
  };
}

export const EXPANDED_COMPETITORS: CompetitorData[] = compactCompetitors.map(expandCompetitor);
